import { useState, useEffect, useMemo, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import { Dialog } from "primereact/dialog";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Tag } from "primereact/tag";
import AvisoMotoristaService from "../../services/resource/avisoTelemetriaService";
import { KPICard } from "../../componentes/chart/KPICard";
import { ChartCard } from "../../componentes/chart/CardChart";
import { CustomTable } from "../../componentes/table/CustomTable";
import DateMasked from "../../componentes/input/DateMasked";
import DropDown from "../../componentes/dropdown/dropdown";
import { intervaloDatas } from "../../utils/options";
import { getComecoAno, getDataSeteDiasAntesFormatado, getHojeFormatado, getMesAtual, getMesPassado, ultimoDiaMesPassado } from "../../utils/formatDate";
import Button from "../../componentes/button/button";
import { Loading } from "../../componentes/loading/loading";
import { captureElementToImage } from "../../utils/captureElementToImage";

// ─── Constantes ──────────────────────────────────────────────────────────────

const TIPO_COLORS = {
    ExcessoRPM: "#f59e0b",
    ExcessoVelocidade: "#ef4444",
    ExcessoTempoParadoLigado: "#8b5cf6",
    FreadaBrusca: "#f97316",
    CurvaAgressiva: "#06b6d4",
    AceleracaoBrusca: "#b206d4",
    PedalAceleradorAcimaLimite: "#06b6d4",
    Default: "#6b7280",
};

const TIPO_ICONS = {
    ExcessoRPM: "⚙️",
    ExcessoVelocidade: "🚀",
    FrenagemBrusca: "🛑",
    AceleracaoBrusca: "⚡",
    CurvaAgressiva: "↩️",
    Default: "⚠️",
};

const fmt = (n) => new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 }).format(n);
const badgeColor = (tipo) => TIPO_COLORS[tipo] ?? TIPO_COLORS.Default;

// ─── Hook: Dialog de detalhamento ────────────────────────────────────────────
// Centraliza o estado do dialog. Cada gráfico chama abrirDialog(titulo, dados).

function useDialogDetalhes() {
    const [visible, setVisible] = useState(false);
    const [dialogTitulo, setDialogTitulo] = useState("");
    const [dialogDados, setDialogDados] = useState([]);

    const abrirDialog = useCallback((titulo, dados) => {
        setDialogTitulo(titulo);
        setDialogDados(dados);
        setVisible(true);
    }, []);

    const fecharDialog = useCallback(() => setVisible(false), []);

    return { visible, dialogTitulo, dialogDados, abrirDialog, fecharDialog };
}

// ─── Dialog de Detalhamento ───────────────────────────────────────────────────

function DialogDetalhes({ visible, onHide, titulo, dados }) {
    const pontuacaoBody = (row) => {
        const v = parseFloat(row.pontuacao ?? 0);
        const severity = v >= 2 ? "danger" : v >= 1 ? "warning" : "info";
        return <Tag value={row.pontuacao} severity={severity} />;
    };

    const tipoBody = (row) => {
        const color = badgeColor(row.tipo);
        const icon = TIPO_ICONS[row.tipo] ?? TIPO_ICONS.Default;
        return (
            <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
                style={{ background: `${color}22`, color }}
            >
                {icon} {row.tipo}
            </span>
        );
    };

    const motoristaBody = (row) => (
        <div>
            <div className="font-semibold text-xs">{row.funcionarioNome?.split(" ").slice(0, 3).join(" ")}</div>
            <div className="text-muted-foreground text-xs">{row.funcionarioCargo}</div>
        </div>
    );

    const headerDialog = (
        <div className="flex items-center gap-2">
            <i className="pi pi-list text-sky-500" />
            <span className="font-bold text-foreground">{titulo}</span>
            <span className="ml-2 text-xs text-muted-foreground font-normal">
                — {dados.length} registro{dados.length !== 1 ? "s" : ""}
            </span>
        </div>
    );

    // Painel extra de velocidade quando há dados de velocidade nos registros
    const comVelocidade = dados.filter((d) => d.velocidadeVeiculo != null && d.velocidadeVia != null);
    const mostrarVelocidade = comVelocidade.length > 0;

    const velSeries = mostrarVelocidade ? [
        { name: "Velocidade Veículo (km/h)", data: comVelocidade.map((d) => d.velocidadeVeiculo) },
        { name: "Limite da Via (km/h)", data: comVelocidade.map((d) => d.velocidadeVia) },
    ] : [];

    const velOptions = {
        chart: { type: "bar", toolbar: { show: false }, animations: { enabled: false }, },
        plotOptions: { bar: { horizontal: false, columnWidth: "55%", borderRadius: 3 } },
        xaxis: {
            categories: comVelocidade.map((d) => d.dataHoraFormatada),
            labels: { style: { colors: "#505864", fontSize: "9px" }, rotate: -30 },
        },
        yaxis: { labels: { style: { colors: "#505864" } }, title: { text: "km/h", style: { color: "#94a3b8" } } },
        colors: ["#ef4444", "#38bdf8"],
        dataLabels: { enabled: false },
        tooltip: {
            theme: "light",
            y: { formatter: (v, { dataPointIndex }) => `${v} km/h (${comVelocidade[dataPointIndex]?.percentualVelocidade?.toFixed(1) ?? "—"}% acima)` },
        },
        legend: { labels: { colors: "#505864" } },
        grid: { borderColor: "#ebebeb" },
    };

    const columns = useMemo(() => [
        {
            key: "dataHoraFormatada",
            label: "Data/Hora",
            sortable: true,
            className: "font-mono text-xs whitespace-nowrap min-w-[110px]",
        },
        {
            key: "funcionarioNome",
            label: "Motorista",
            sortable: true,
            className: "min-w-[160px]",
            render: (_, row) => motoristaBody(row)
        },
        {
            key: "descricaoVeiculo",
            label: "Veículo",
            sortable: true,
            className: "min-w-[110px] font-mono text-xs",
            render: (v) => v?.split("/")[0]?.trim()
        },
        {
            key: "tipo",
            label: "Tipo",
            sortable: true,
            className: "min-w-[180px]",
            render: (v, row) => tipoBody(row)
        },
        {
            key: "pontuacao",
            label: "Pontuação",
            sortable: true,
            center: true,
            className: "min-w-[90px]",
            render: (v, row) => pontuacaoBody(row)
        },
        {
            key: "descricao",
            label: "Descrição",
            sortable: false,
            className: "min-w-[220px] text-xs",
            render: (v, row) => (
                <span title={row.descricao}>
                    {row.descricao}
                </span>
            )
        }
    ], []);

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={headerDialog}
            className="w-[92%]"
            blockScroll
            pt={{
                root: { className: "rounded-2xl overflow-hidden shadow-pop" },
                header: { className: "border-b border-border py-4 px-6 bg-card" },
                content: { className: "px-6 py-2 bg-muted" },
            }}
        >

            <CustomTable
                data={dados}
                columns={columns}
                pageSize={20}
                pagination
                exportable
                maxHeight={390}
                exportFilename={titulo}
                className="text-xs"

            />

            {/* Painel de velocidades — só aparece quando os dados têm velocidade */}
            {mostrarVelocidade && (
                <div className="mt-6 border-t border-border pt-5">
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
                        🚀 Velocidades registradas
                    </p>
                    <ReactApexChart type="bar" series={velSeries} options={velOptions} height={200} />
                </div>
            )}
        </Dialog>
    );
}

// ─── Hint visual nos títulos dos gráficos ────────────────────────────────────

function HintClicavel() {
    return (
        <span className="ml-1 text-[10px] text-muted-foreground font-normal normal-case tracking-normal">
            (clique para detalhar)
        </span>
    );
}

// ─── Gráficos ─────────────────────────────────────────────────────────────────

function ChartInfracoesPorTipo({ data, onSliceClick }) {
    const counts = useMemo(() => {
        const map = {};
        data.forEach(({ tipo }) => { map[tipo] = (map[tipo] ?? 0) + 1; });
        return map;
    }, [data]);

    const labels = Object.keys(counts);
    const series = Object.values(counts);
    const colors = labels.map((t) => badgeColor(t));

    const options = {
        chart: {
            type: "donut",
            toolbar: { show: false },
            animations: { enabled: false },
            events: {
                dataPointSelection: (_e, _ctx, config) => {
                    const tipo = labels[config.dataPointIndex];
                    if (!tipo) return;
                    onSliceClick(`Infrações — ${tipo}`, data.filter((d) => d.tipo === tipo));
                },
            },
        },
        labels,
        colors,
        legend: { labels: { colors: "#505864" }, fontFamily: "inherit" },
        stroke: { width: 0 },
        plotOptions: {
            pie: {
                donut: {
                    size: "65%",
                    labels: {
                        show: true,
                        total: { show: true, label: "Total", color: "#505864", fontSize: "13px", formatter: () => series.reduce((a, b) => a + b, 0) },
                    },
                },
            },
        },
        dataLabels: { enabled: false },
        tooltip: { theme: "light" },
        states: { active: { filter: { type: "darken", value: 0.85 } } },
    };

    return (
        <ChartCard title={<>Infrações por Tipo <HintClicavel /></>} view="chart">
            <div className="cursor-pointer">
                <ReactApexChart type="donut" series={series} options={options} height={260} />
            </div>
        </ChartCard>
    );
}

function ChartInfracoesPorDia({ data, onDayClick }) {
    const byDay = useMemo(() => {
        const map = {};
        data.forEach(({ dataOcorrido }) => { map[dataOcorrido] = (map[dataOcorrido] ?? 0) + 1; });
        const sorted = Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
        return { cats: sorted.map(([d]) => d), vals: sorted.map(([, v]) => v) };
    }, [data]);

    const options = {
        chart: {
            type: "area",
            animations: { enabled: false },
            toolbar: { show: false },
            events: {
                dataPointSelection: (_e, _ctx, config) => {
                    const dia = byDay.cats[config.dataPointIndex];
                    if (!dia) return;
                    onDayClick(`Ocorrências em ${dia}`, data.filter((d) => d.dataOcorrido === dia));
                },
            },
        },
        xaxis: { categories: byDay.cats, labels: { style: { colors: "#505864", fontSize: "11px" } }, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { labels: { style: { colors: "#505864" } } },
        grid: { borderColor: "#ebebeb", strokeDashArray: 0 },
        stroke: { curve: "smooth", width: 2 },
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 100] } },
        colors: ["#38bdf8"],
        dataLabels: { enabled: false },
        tooltip: { theme: "light" },
        markers: { size: 5, hover: { size: 7 } },
    };

    return (
        <ChartCard title={<>Ocorrências por Dia <HintClicavel /></>} view="chart">
            <div className="cursor-pointer">
                <ReactApexChart type="area" series={[{ name: "Infrações", data: byDay.vals }]} options={options} height={260} />
            </div>
        </ChartCard>
    );
}

function ChartPorMotorista({ data, onBarClick }) {
    const byDriver = useMemo(() => {
        const map = {};
        data.forEach(({ funcionarioNome, pontuacao }) => {
            if (!map[funcionarioNome]) map[funcionarioNome] = { qtd: 0, pts: 0 };
            map[funcionarioNome].qtd += 1;
            map[funcionarioNome].pts += parseFloat(pontuacao ?? 0);
        });
        return Object.entries(map).sort(([, a], [, b]) => b.pts - a.pts).slice(0, 8);
    }, [data]);

    const nomesFull = byDriver.map(([n]) => n);
    const names = byDriver.map(([n]) => n.split(" ").slice(0, 2).join(" "));
    const qtds = byDriver.map(([, v]) => v.qtd);
    const pts = byDriver.map(([, v]) => +v.pts.toFixed(2));

    const options = {
        chart: {
            type: "bar",
            animations: { enabled: false },
            background: "transparent",
            toolbar: { show: false },
            events: {
                dataPointSelection: (_e, _ctx, config) => {
                    const nomeFull = nomesFull[config.dataPointIndex];
                    if (!nomeFull) return;
                    onBarClick(
                        `Infrações — ${nomeFull.split(" ").slice(0, 3).join(" ")}`,
                        data.filter((d) => d.funcionarioNome === nomeFull)
                    );
                },
            },
        },
        plotOptions: { bar: { horizontal: true, barHeight: "55%", borderRadius: 4 } },
        xaxis: { categories: names, labels: { style: { colors: "#505864", fontSize: "11px" } }, axisBorder: { show: false } },
        yaxis: { labels: { style: { colors: "#505864", fontSize: "11px" } } },
        grid: { borderColor: "#ebebeb", strokeDashArray: 0 },
        colors: ["#f59e0b", "#ef4444"],
        dataLabels: { enabled: false },
        legend: { labels: { colors: "#505864" } },
        tooltip: { theme: "light" },
        states: { active: { filter: { type: "darken", value: 0.85 } } },
    };

    return (
        <ChartCard title={<>Ranking de Motoristas <HintClicavel /></>} view="chart">
            <div className="cursor-pointer">
                <ReactApexChart
                    type="bar"
                    series={[{ name: "Qtd. Infrações", data: qtds }, { name: "Pontuação", data: pts }]}
                    options={options}
                />
            </div>
        </ChartCard>
    );
}

function ChartRadarTipos({ data, onPointClick }) {
    const counts = useMemo(() => {
        const map = {};
        data.forEach(({ tipo }) => { map[tipo] = (map[tipo] ?? 0) + 1; });
        return map;
    }, [data]);

    const tipos = Object.keys(counts);
    const series = [{ name: "Ocorrências", data: Object.values(counts) }];

    const options = {
        chart: {
            type: "radar",
            animations: { enabled: false },
            toolbar: { show: false },
            events: {
                dataPointSelection: (_e, _ctx, config) => {
                    const tipo = tipos[config.dataPointIndex];
                    if (!tipo) return;
                    onPointClick(`Infrações — ${tipo}`, data.filter((d) => d.tipo === tipo));
                },
            },
        },
        xaxis: { categories: tipos, labels: { style: { colors: Array(tipos.length).fill("#737579"), fontSize: "11px" } } },
        yaxis: { show: false },
        fill: { opacity: 0.2 },
        stroke: { width: 2 },
        markers: { size: 4, hover: { size: 7 } },
        colors: ["#38bdf8"],
        dataLabels: { enabled: false },
        tooltip: { theme: "light" },
        plotOptions: { radar: { polygons: { strokeColors: "#e2e8f0", fill: { colors: ["rgba(241,245,249,0.8)", "rgba(248,250,252,0.8)"] } } } },
    };

    return (
        <ChartCard title={<>Distribuição Radar <HintClicavel /></>} view="chart">
            <div className="cursor-pointer">
                <ReactApexChart type="radar" series={series} options={options} />
            </div>
        </ChartCard>
    );
}

function ChartTiposPorMotorista({ data, onBarClick }) {
    const { motoristasNomes, motoristasFull, series } = useMemo(() => {
        const drivers = {};
        const tiposSet = new Set();
        data.forEach(({ funcionarioNome, tipo }) => {
            if (!drivers[funcionarioNome]) drivers[funcionarioNome] = {};
            drivers[funcionarioNome][tipo] = (drivers[funcionarioNome][tipo] ?? 0) + 1;
            tiposSet.add(tipo);
        });
        const tipos = [...tiposSet].sort();
        const motoristasFull = Object.keys(drivers).sort();
        const motoristasNomes = motoristasFull.map((n) => n.split(" ").slice(0, 2).join(" "));
        const series = tipos.map((tipo) => ({
            name: tipo,
            data: motoristasFull.map((m) => drivers[m][tipo] ?? 0),
            color: badgeColor(tipo),
        }));
        return { motoristasNomes, motoristasFull, series };
    }, [data]);

    const options = {
        chart: {
            type: "bar",
            animations: { enabled: false },
            stacked: true,
            background: "transparent",
            toolbar: { show: false },
            events: {
                dataPointSelection: (_e, _ctx, config) => {
                    const nomeFull = motoristasFull[config.dataPointIndex];
                    const tipoSeries = series[config.seriesIndex]?.name;
                    if (!nomeFull) return;
                    const filtrado = data.filter(
                        (d) => d.funcionarioNome === nomeFull && (!tipoSeries || d.tipo === tipoSeries)
                    );
                    const label = tipoSeries
                        ? `${nomeFull.split(" ").slice(0, 2).join(" ")} — ${tipoSeries}`
                        : nomeFull.split(" ").slice(0, 3).join(" ");
                    onBarClick(label, filtrado);
                },
            },
        },
        plotOptions: { bar: { horizontal: false, borderRadius: 3, columnWidth: "55%" } },
        xaxis: {
            categories: motoristasNomes,
            labels: { style: { colors: "#505864", fontSize: "10px" }, rotate: -20 },
            axisBorder: { show: false },
        },
        yaxis: { labels: { style: { colors: "#505864" } } },
        grid: { borderColor: "#ebebeb", strokeDashArray: 0 },
        legend: { labels: { colors: "#505864" }, position: "top", fontSize: "11px" },
        dataLabels: { enabled: false },
        tooltip: { theme: "light" },
        fill: { opacity: 0.9 },
        states: { active: { filter: { type: "darken", value: 0.85 } } },
    };

    return (
        <ChartCard title={<>Tipos de Infração por Motorista <HintClicavel /></>} view="chart">
            <div className="cursor-pointer">
                <ReactApexChart type="bar" series={series} options={options} height={280} />
            </div>
        </ChartCard>
    );
}

// ─── Filtros de Motorista e Tipo ─────────────────────────────────────────────

function FiltrosMotoristaETipo({ data, filtroMotorista, setFiltroMotorista, filtrosTipo, setFiltrosTipo }) {
    const motoristas = useMemo(() => {
        const map = {};
        data.forEach(({ funcionarioId, funcionarioNome }) => { map[funcionarioId] = funcionarioNome; });
        return Object.entries(map).sort(([, a], [, b]) => a.localeCompare(b));
    }, [data]);

    const tipos = useMemo(() => [...new Set(data.map((d) => d.tipo))].sort(), [data]);

    const toggleTipo = (tipo) =>
        setFiltrosTipo((prev) => prev.includes(tipo) ? prev.filter((t) => t !== tipo) : [...prev, tipo]);

    return (
        <div className="rounded-2xl p-5 border border-border shadow-card mb-6 bg-card">
            <div className="flex flex-wrap gap-6 items-end">
                {/* Select motorista */}
                <div className="flex flex-col gap-1 min-w-[220px]">
                    <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                        👤 Filtrar Motorista
                    </label>
                    <select
                        value={filtroMotorista}
                        onChange={(e) => setFiltroMotorista(e.target.value)}
                        className="rounded-xl px-3 py-2 text-sm border border-input text-foreground bg-muted focus:outline-none focus:ring-2 focus:ring-sky-400/40 transition-all cursor-pointer"
                    >
                        <option value="">Todos os motoristas</option>
                        {motoristas.map(([id, nome]) => (
                            <option key={id} value={id}>
                                {nome.split(" ").slice(0, 3).join(" ")}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Chips de tipo */}
                <div className="flex flex-col gap-2 flex-1">
                    <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                        ⚡ Tipo de Infração
                    </label>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => setFiltrosTipo([])}
                            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${filtrosTipo.length === 0
                                ? "bg-sky-500 border-sky-500 text-white shadow shadow-sky-200"
                                : "border-input text-muted-foreground hover:border-border hover:text-foreground"
                                }`}
                        >
                            Todos
                        </button>
                        {tipos.map((tipo) => {
                            const color = badgeColor(tipo);
                            const icon = TIPO_ICONS[tipo] ?? TIPO_ICONS.Default;
                            const active = filtrosTipo.includes(tipo);
                            return (
                                <button
                                    key={tipo}
                                    onClick={() => toggleTipo(tipo)}
                                    className="px-3 py-1 rounded-full text-xs font-semibold border transition-all"
                                    style={
                                        active
                                            ? { background: `${color}22`, border: `1px solid ${color}88`, color }
                                            : { background: "transparent", border: "1px solid #e2e8f0", color: "#64748b" }
                                    }
                                >
                                    {icon} {tipo}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {(filtroMotorista || filtrosTipo.length > 0) && (
                    <button
                        onClick={() => { setFiltroMotorista(""); setFiltrosTipo([]); }}
                        className="px-3 py-2 rounded-xl text-xs font-semibold text-muted-foreground border border-input hover:bg-muted transition-all"
                    >
                        ✕ Limpar
                    </button>
                )}
            </div>

            {(filtroMotorista || filtrosTipo.length > 0) && (
                <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-2 items-center">
                    <span className="text-xs text-muted-foreground">Filtrando por:</span>
                    {filtroMotorista && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-sky-50 text-sky-600 border border-sky-200">
                            👤 {data.find(d => d.funcionarioId === filtroMotorista)?.funcionarioNome?.split(" ").slice(0, 2).join(" ")}
                        </span>
                    )}
                    {filtrosTipo.map(t => (
                        <span key={t} className="text-xs px-2 py-0.5 rounded-full"
                            style={{ background: `${badgeColor(t)}15`, color: badgeColor(t), border: `1px solid ${badgeColor(t)}44` }}>
                            {TIPO_ICONS[t] ?? "⚠️"} {t}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Tabela Principal ─────────────────────────────────────────────────────────

function SectionTitle({ children }) {
    return <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">{children}</h2>;
}

function Badge({ tipo }) {
    const color = badgeColor(tipo);
    const icon = TIPO_ICONS[tipo] ?? TIPO_ICONS.Default;
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{ background: `${color}22`, color }}>
            {icon} {tipo}
        </span>
    );
}

function TabelaInfracoes({ data }) {
    const columns = useMemo(() => [
        { key: "dataHoraFormatada", label: "Data/Hora", sortable: true, className: "font-mono text-xs whitespace-nowrap" },
        {
            key: "funcionarioNome", label: "Motorista", sortable: true,
            render: (_, row) => (
                <div>
                    <div className="font-medium text-xs leading-tight">{row.funcionarioNome?.split(" ").slice(0, 3).join(" ")}</div>
                    <div className="text-muted-foreground text-xs">{row.funcionarioCargo}</div>
                </div>
            ),
        },
        { key: "descricaoVeiculo", label: "Veículo", sortable: true, className: "text-xs font-mono whitespace-nowrap", render: (v) => v?.split("/")[0]?.trim() },
        { key: "tipo", label: "Tipo", sortable: true, render: (v) => <Badge tipo={v} /> },
        {
            key: "pontuacao", label: "Pontuação", sortable: true, center: true,
            render: (v) => (
                <span className="font-bold" style={{ color: parseFloat(v) >= 2 ? "#ef4444" : parseFloat(v) >= 1 ? "#f59e0b" : "#22d3ee" }}>
                    {v}
                </span>
            ),
        },
        { key: "descricao", label: "Descrição", sortable: true, className: "text-xs max-w-xs truncate", render: (v) => <span title={v}>{v}</span> },
    ], []);

    return (
        <div className="rounded-2xl border border-border shadow-card overflow-hidden bg-card">
            <div className="p-5 border-b border-border">
                <SectionTitle>Detalhamento das Infrações</SectionTitle>
            </div>
            <CustomTable data={data} columns={columns} pageSize={20}
                emptyMessage="Nenhuma infração encontrada para os filtros selecionados."
                className="text-sm" striped pagination hover />
        </div>
    );
}

// ─── Filtro de Datas ──────────────────────────────────────────────────────────

function DateRangePicker({ dataInicio, dataFim, onChange, onBuscar, loading, setErroAtualizacao, setDialogAtualizar }) {
    const [filtro, setFiltro] = useState(false);
    const [dataSelecionada, setDataSelecionada] = useState(7);

    const mudarPeriodoDeDatas = (e) => {
        const valor = e.target.value;
        setDataSelecionada(valor);
        if (valor === 14) { setFiltro(true); return; }
        setFiltro(false);
        if (valor === 7) { onChange("dataInicio", getMesAtual()); onChange("dataFim", getHojeFormatado()); }
        else if (valor === 30) { onChange("dataInicio", getMesPassado()); onChange("dataFim", ultimoDiaMesPassado()); }
        else if (valor === 365) { onChange("dataInicio", getComecoAno()); onChange("dataFim", getHojeFormatado()); }
        else { onChange("dataInicio", getDataSeteDiasAntesFormatado()); }
    };

    return (
        <div className="flex flex-wrap items-center gap-3">



            <i title="Buscar Atualização" className="fa fa-sync text-muted-foreground mt-2  cursor-pointer" onClick={() => { setErroAtualizacao(null); setDialogAtualizar(true); }} />
            <i title="Gerar imagem" className="fa fa-image text-blue-600 mx-2 mt-2  cursor-pointer" onClick={() => captureElementToImage("dashboard-infracao", 'Relatório de Infrações de Caxias')} />

            <DropDown optional options={intervaloDatas} optionLabel="label" optionValue="value"
                onChange={mudarPeriodoDeDatas} value={dataSelecionada} className="w-60" filter={false} />

            {filtro && (
                <DateMasked onChange={(e) => onChange("dataInicio", e)} value={dataInicio} />
            )}

            {filtro && (
                <DateMasked onChange={(e) => onChange("dataFim", e)} value={dataFim} />
            )}

            <div className={`flex flex-col`}>
                <label></label>
                <Button label={loading ? "Buscando…" : "Buscar"} onClick={onBuscar} iconLeft="fa fa-search" disabled={loading} />

            </div>
        </div>
    );
}

// ─── Dashboard Principal ──────────────────────────────────────────────────────

export default function DashboardInfracoesCaxias() {
    const service = new AvisoMotoristaService();

    const [filters, setFilters] = useState({ dataInicio: getMesAtual(), dataFim: getHojeFormatado() });
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [filtroMotorista, setFiltroMotorista] = useState("");
    const [filtrosTipo, setFiltrosTipo] = useState([]);

    const [dialogAtualizar, setDialogAtualizar] = useState(false);
    const [atualizando, setAtualizando] = useState(false);
    const [erroAtualizacao, setErroAtualizacao] = useState(null);
    const [periodoAtualizacao, setPeriodoAtualizacao] = useState(() => {
        const hoje = new Date();
        return { ano: hoje.getFullYear(), mes: hoje.getMonth() + 1 };
    });

    const labelPeriodoAtualizacao = useMemo(() => {
        const { ano, mes } = periodoAtualizacao;
        const dataInicio = new Date(ano, mes - 1, 25);
        const dataFim = new Date(ano, mes, 24); // mês seguinte, dia 24
        const fmt = (d) =>
            d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
        return `${fmt(dataInicio)} até ${fmt(dataFim)}`;
    }, [periodoAtualizacao]);

    const handleAtualizar = async () => {
        setAtualizando(true);
        setErroAtualizacao(null);
        try {
            await service.atualizar(periodoAtualizacao); // já tem { ano, mes }
            setDialogAtualizar(false);
            await fetchData();
        } catch (e) {
            setErroAtualizacao(`Erro ao atualizar: ${e.message}`);
        } finally {
            setAtualizando(false);
        }
    };

    // ── Dialog ──
    const { visible, dialogTitulo, dialogDados, abrirDialog, fecharDialog } = useDialogDetalhes();

    const handleFilterChange = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        setFiltroMotorista("");
        setFiltrosTipo([]);
        try {
            const res = await service.findAll({ dataInicial: filters.dataInicio, dataFinal: filters.dataFim });
            setData(res.data);
        } catch (e) {
            setError(`API indisponível (${e.message}).`);
        } finally {
            setLoading(false);
        }
    };

    const dadosFiltrados = useMemo(() => data.filter((d) => {
        const passaMotorista = !filtroMotorista || d.funcionarioId === filtroMotorista;
        const passaTipo = filtrosTipo.length === 0 || filtrosTipo.includes(d.tipo);
        return passaMotorista && passaTipo;
    }), [data, filtroMotorista, filtrosTipo]);

    const kpis = useMemo(() => {
        const totalInfracoes = dadosFiltrados.length;
        const totalPontuacao = dadosFiltrados.reduce((s, d) => s + parseFloat(d.pontuacao ?? 0), 0);
        const motoristasUnicos = new Set(dadosFiltrados.map((d) => d.funcionarioId)).size;
        const tipoMaisFreq = Object.entries(
            dadosFiltrados.reduce((acc, { tipo }) => { acc[tipo] = (acc[tipo] ?? 0) + 1; return acc; }, {})
        ).sort(([, a], [, b]) => b - a)[0];
        const veiculosUnicos = new Set(dadosFiltrados.map((d) => d.descricaoVeiculo.split("/")[0].trim())).size;
        return { totalInfracoes, totalPontuacao, motoristasUnicos, tipoMaisFreq, veiculosUnicos };
    }, [dadosFiltrados]);

    return (
        <div className="min-h-screen text-foreground font-sans">
            <div className="fixed inset-0 pointer-events-none opacity-[0.03]" />

            <div className="relative z-10 max-w-[1400px] mx-auto px-4 py-8">

                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-8">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <span className="text-3xl">🚦</span>
                            <h1 className="text-2xl font-bold tracking-tight text-foreground">Dashboard de Infrações</h1>
                        </div>
                        <p className="text-muted-foreground text-sm ml-11">Monitoramento de conduta de motoristas</p>
                    </div>
                    <DateRangePicker
                        dataInicio={filters.dataInicio}
                        dataFim={filters.dataFim}
                        onChange={handleFilterChange}
                        onBuscar={fetchData}
                        loading={loading}
                        setDialogAtualizar={setDialogAtualizar}
                        setErroAtualizacao={setAtualizando}
                    />
                </div>

                {loading ?
                    <Loading />
                    :

                    <div className="fade-in">

                        {error && (
                            <div className="mb-5 flex items-center gap-3 rounded-xl px-4 py-3 text-xs text-amber-700 bg-amber-50 border border-amber-200">
                                <span className="text-base">⚠️</span>
                                <span>{error}</span>
                            </div>
                        )}

                        {/* Filtros locais */}
                        {data.length > 0 && (
                            <FiltrosMotoristaETipo
                                data={data}
                                filtroMotorista={filtroMotorista}
                                setFiltroMotorista={setFiltroMotorista}
                                filtrosTipo={filtrosTipo}
                                setFiltrosTipo={setFiltrosTipo}
                            />
                        )}

                        <div id="dashboard-infracao">

                            {/* KPIs */}
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                                <KPICard label="Total de Infrações" value={kpis.totalInfracoes} icon="📋" color="#38bdf8" />
                                <KPICard label="Pontuação Total" value={fmt(kpis.totalPontuacao)} sub="soma dos pontos" icon="📊" color="#f59e0b" />
                                <KPICard label="Motoristas" value={kpis.motoristasUnicos} sub="envolvidos" icon="👤" color="#a78bfa" />
                                <KPICard label="Veículos" value={kpis.veiculosUnicos} sub="envolvidos" icon="🚛" color="#34d399" />
                                <KPICard
                                    label="Tipo Principal"
                                    value={kpis.tipoMaisFreq ? kpis.tipoMaisFreq[1] : 0}
                                    sub={kpis.tipoMaisFreq ? kpis.tipoMaisFreq[0] : "—"}
                                    icon={kpis.tipoMaisFreq ? (TIPO_ICONS[kpis.tipoMaisFreq[0]] ?? "⚠️") : "⚠️"}
                                    color="#ef4444"
                                />
                            </div>

                            {/* Gráficos linha 1 */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <ChartInfracoesPorTipo data={dadosFiltrados} onSliceClick={abrirDialog} />
                                <ChartInfracoesPorDia data={dadosFiltrados} onDayClick={abrirDialog} />
                            </div>

                            {/* Gráficos linha 2 */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <ChartPorMotorista data={dadosFiltrados} onBarClick={abrirDialog} />
                                <ChartRadarTipos data={dadosFiltrados} onPointClick={abrirDialog} />
                            </div>

                            {/* Tipos por Motorista — full width */}
                            <div className="mb-6">
                                <ChartTiposPorMotorista data={dadosFiltrados} onBarClick={abrirDialog} />
                            </div>
                        </div>

                        {/* Tabela principal */}
                        <TabelaInfracoes data={dadosFiltrados} />

                        {/* Footer */}
                        <div className="mt-6 text-center text-xs text-muted-foreground">
                            {dadosFiltrados.length} de {data.length} registros · período {filters.dataInicio} → {filters.dataFim}
                        </div>
                    </div>
                }
            </div>

            {/* ── Dialog de Detalhamento ── */}
            <DialogDetalhes
                visible={visible}
                onHide={fecharDialog}
                titulo={dialogTitulo}
                dados={dialogDados}
            />

            {dialogAtualizar && (
                <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 backdrop-blur-xs">
                    <div className="bg-card rounded-2xl shadow-pop p-6 w-full max-w-md mx-4">

                        {/* Título */}
                        <div className="flex items-center gap-3 mb-5">
                            <span className="text-2xl">🔄</span>
                            <div>
                                <h2 className="text-base font-semibold text-foreground">Atualizar Infrações</h2>
                                <p className="text-xs text-muted-foreground">Selecione o período de referência</p>
                            </div>
                        </div>

                        {/* Seletores Ano / Mês */}
                        <div className="flex gap-3 mb-4">
                            <div className="flex-1">
                                <label className="block text-xs font-medium text-muted-foreground mb-1">Ano</label>
                                <select
                                    value={periodoAtualizacao.ano}
                                    onChange={(e) =>
                                        setPeriodoAtualizacao((p) => ({ ...p, ano: Number(e.target.value) }))
                                    }
                                    className="w-full rounded-lg border border-input px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                                >
                                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((a) => (
                                        <option key={a} value={a}>{a}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex-1">
                                <label className="block text-xs font-medium text-muted-foreground mb-1">Mês</label>
                                <select
                                    value={periodoAtualizacao.mes}
                                    onChange={(e) =>
                                        setPeriodoAtualizacao((p) => ({ ...p, mes: Number(e.target.value) }))
                                    }
                                    className="w-full rounded-lg border border-input px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                                >
                                    {[
                                        "Janeiro", "Fevereiro", "Março", "Abril",
                                        "Maio", "Junho", "Julho", "Agosto",
                                        "Setembro", "Outubro", "Novembro", "Dezembro"
                                    ].map((nome, i) => (
                                        <option key={i + 1} value={i + 1}>{nome}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Preview do período */}
                        <div className="flex items-center gap-2 rounded-xl bg-blue-50 border border-blue-100 px-4 py-3 mb-5">
                            <span className="text-base">📅</span>
                            <div>
                                <p className="text-xs text-blue-400 font-medium uppercase tracking-wide">Período</p>
                                <p className="text-sm text-blue-700 font-semibold">{labelPeriodoAtualizacao}</p>
                            </div>
                        </div>

                        {/* Erro */}
                        {erroAtualizacao && (
                            <div className="mb-4 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                                ⚠️ {erroAtualizacao}
                            </div>
                        )}

                        {/* Ações */}
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => { setDialogAtualizar(false); setErroAtualizacao(null); }}
                                disabled={atualizando}
                                className="px-4 py-2 text-sm rounded-lg border border-input text-muted-foreground hover:bg-muted disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleAtualizar}
                                disabled={atualizando}
                                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2"
                            >
                                {atualizando ? (
                                    <>
                                        <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                        Atualizando...
                                    </>
                                ) : (
                                    "Confirmar"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}